import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/auth/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import CustomersPage from './pages/customers/CustomersPage';
import ProductsPage from './pages/products/ProductsPage';
import OffersPage from './pages/offers/OffersPage';
import NewOfferPage from './pages/offers/NewOfferPage';
import EditOfferPage from './pages/offers/EditOfferPage';
import OfferDetailPage from './pages/offers/OfferDetailPage';
import AdminPage from './pages/admin/AdminPage';
import StatsPage from './pages/admin/StatsPage';
import PriceListPage from './pages/pricelist/PriceListPage';
import MarketParamsPage from './pages/admin/MarketParamsPage';
import ComparisonPage from './pages/comparison/ComparisonPage';
import CompetitorPricesPage from './pages/admin/CompetitorPricesPage';
import MaterialsPage from './pages/materials/MaterialsPage';
import Layout from './components/layout/Layout';

const theme = createTheme({
  palette: {
    primary: { main: '#1565c0' },
    secondary: { main: '#f57c00' },
  },
});

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  return token ? <>{children}</> : <Navigate to="/login" />;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, isAdmin } = useAuth();
  if (!token) return <Navigate to="/login" />;
  if (!isAdmin) return <Navigate to="/dashboard" />;
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }>
              <Route index element={<Navigate to="/dashboard" />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="offers" element={<OffersPage />} />
              <Route path="price-list" element={<PriceListPage />} />
              <Route path="offers/new" element={<NewOfferPage />} />
              <Route path="offers/:id" element={<OfferDetailPage />} />
              <Route path="offers/:id/edit" element={<EditOfferPage />} />
              <Route path="comparison" element={<ComparisonPage />} />
              <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
              <Route path="admin/stats" element={<AdminRoute><StatsPage /></AdminRoute>} />
              <Route path="admin/market-params" element={<AdminRoute><MarketParamsPage /></AdminRoute>} />
              <Route path="admin/competitor-prices" element={<AdminRoute><CompetitorPricesPage /></AdminRoute>} />
              <Route path="materials" element={<AdminRoute><MaterialsPage /></AdminRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
